import {
  DELIVERABLE_FAILURE_DESCRIPTION_FALLBACK,
  DELIVERABLE_FAILURE_REASON_DESCRIPTIONS,
  DELIVERABLE_FAILURE_REASON_FALLBACK,
  DELIVERABLE_FAILURE_REASON_LABELS,
} from "@/lib/errorMessages";
import type {
  DeliverableBuildStage,
  DeliverableFailureDetail,
  DeliverableFailureReason,
  DeliverableListItem,
  DeliverableStatus,
} from "@/types";

interface DeliverableLifecycleFields {
  status?: DeliverableStatus;
  buildStage?: DeliverableBuildStage | null;
  failureReason?: DeliverableFailureReason | null;
  failureDetail?: DeliverableFailureDetail | null;
  currentRevisionId?: string | null;
}

type JsonRecord = { [key: string]: DeliverableFailureDetail };

const STAGE_LABELS: Record<string, string> = {
  queued: "queued",
  analyzing: "analyzing",
  designing: "designing",
  rendering: "rendering",
  finalizing: "finalizing",
};

const FAILURE_DETAIL_MAX_CHARS = 280;
const FAILURE_DETAIL_MAX_PARTS = 3;

const FAILURE_DETAIL_MESSAGE_KEYS = [
  "message",
  "detail",
  "description",
  "error",
];

const FAILURE_DETAIL_COLLECTION_KEYS = [
  "acceptanceIssues",
  "violations",
  "issues",
  "errors",
];

const compactText = (value: string): string | null => {
  const compact = value.replace(/\s+/g, " ").trim();
  return compact || null;
};

const truncateText = (value: string): string =>
  value.length > FAILURE_DETAIL_MAX_CHARS
    ? `${value.slice(0, FAILURE_DETAIL_MAX_CHARS - 3).trimEnd()}...`
    : value;

const isJsonRecord = (
  value: DeliverableFailureDetail | null | undefined,
): value is JsonRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const humanizeMachineLabel = (value: string): string | null => {
  const compact = compactText(value.replace(/[-_]+/g, " "));
  return compact?.toLowerCase() ?? null;
};

/**
 * A reason with no label of its own reads as the generic one. Title-casing the
 * enum instead is what put "Build Failed" in front of an advisor: the enum is
 * an internal name, and Title Case is not the app's sentence case either.
 */
const fallbackFailureReasonLabel = (
  _reason: DeliverableFailureReason | null | undefined,
): string => DELIVERABLE_FAILURE_REASON_FALLBACK;

const getStringValue = (
  value: DeliverableFailureDetail | null | undefined,
): string | null => (typeof value === "string" ? compactText(value) : null);

const getFirstStringField = (
  detail: JsonRecord,
  keys: string[],
): string | null => {
  for (const key of keys) {
    const value = getStringValue(detail[key]);
    if (value) return value;
  }

  return null;
};

const uniqueCompactParts = (parts: (string | null)[]): string[] => {
  const seen = new Set<string>();
  const unique: string[] = [];

  for (const part of parts) {
    const compact = part ? truncateText(part) : null;
    if (!compact || seen.has(compact)) continue;

    seen.add(compact);
    unique.push(compact);
  }

  return unique;
};

const formatIssueEntry = (issue: DeliverableFailureDetail): string | null => {
  if (typeof issue === "string") {
    return compactText(issue);
  }

  if (typeof issue === "number" || typeof issue === "boolean") {
    return String(issue);
  }

  if (isJsonRecord(issue)) {
    const message = getFirstStringField(issue, FAILURE_DETAIL_MESSAGE_KEYS);
    if (message) return message;

    const code = getStringValue(issue.code);
    if (code) return humanizeMachineLabel(code);
  }

  return null;
};

const getIssueMessages = (detail: JsonRecord): string[] =>
  FAILURE_DETAIL_COLLECTION_KEYS.flatMap((key) => {
    const value = detail[key];
    if (!Array.isArray(value)) return [];

    return value.map(formatIssueEntry).filter(Boolean) as string[];
  });

const getHorizontalOverflowMessage = (
  viewportName: "desktop" | "mobile",
  detail: JsonRecord,
): string | null => {
  const viewport = detail[viewportName];
  if (!isJsonRecord(viewport)) return null;

  const overflow = viewport.horizontalOverflowPx;
  if (typeof overflow !== "number" || overflow <= 0) return null;

  return `The ${viewportName} document overflows horizontally by ${overflow}px.`;
};

export const formatDeliverableFailureDetail = (
  detail: DeliverableFailureDetail | null | undefined,
): string | null => {
  if (detail === null || detail === undefined) return null;

  if (typeof detail === "string") {
    return compactText(detail);
  }

  if (typeof detail === "number" || typeof detail === "boolean") {
    return String(detail);
  }

  if (Array.isArray(detail)) {
    const parts = uniqueCompactParts(detail.map(formatIssueEntry));
    return parts.length ? truncateText(parts.join("; ")) : null;
  }

  const parts = uniqueCompactParts([
    getFirstStringField(detail, FAILURE_DETAIL_MESSAGE_KEYS),
    ...getIssueMessages(detail),
    getHorizontalOverflowMessage("desktop", detail),
    getHorizontalOverflowMessage("mobile", detail),
  ]);

  if (parts.length) {
    return truncateText(parts.slice(0, FAILURE_DETAIL_MAX_PARTS).join("; "));
  }

  return truncateText(JSON.stringify(detail));
};

export const getDeliverableStageLabel = (
  stage: DeliverableBuildStage | null | undefined,
): string | null =>
  stage ? (STAGE_LABELS[stage] ?? humanizeMachineLabel(stage)) : null;

export const getDeliverableFailureReasonLabel = (
  reason: DeliverableFailureReason | null | undefined,
): string =>
  DELIVERABLE_FAILURE_REASON_LABELS[reason ?? "UNKNOWN"] ??
  fallbackFailureReasonLabel(reason);

const hasFailed = (deliverable: DeliverableLifecycleFields): boolean =>
  deliverable.status === "FAILED" || deliverable.status === "FAILED_VALIDATION";

/** The short label for how a terminal row failed. */
export const getDeliverableFailureHeadline = (
  deliverable: DeliverableLifecycleFields,
): string | null =>
  hasFailed(deliverable)
    ? getDeliverableFailureReasonLabel(deliverable.failureReason)
    : null;

/**
 * The sentence that says what to do about it. The row's `failureDetail` is
 * deliberately not consulted: it carries service names and endpoint paths, and
 * an advisor cannot act on either.
 */
export const getDeliverableFailureDescription = (
  deliverable: DeliverableLifecycleFields,
): string | null => {
  if (!hasFailed(deliverable)) return null;

  return (
    DELIVERABLE_FAILURE_REASON_DESCRIPTIONS[
      deliverable.failureReason ?? "UNKNOWN"
    ] ?? DELIVERABLE_FAILURE_DESCRIPTION_FALLBACK
  );
};

export const hasDeliverableListArtifact = (
  deliverable: DeliverableLifecycleFields,
): boolean =>
  deliverable.status === "COMPLETE" ||
  deliverable.status === "FAILED_VALIDATION" ||
  Boolean(deliverable.currentRevisionId);

export const canOpenDeliverableListItem = (
  deliverable: DeliverableListItem,
): boolean => hasDeliverableListArtifact(deliverable);
